import { useEffect, useRef } from 'react';

import { addDatadogRUMGlobalContext } from '@opendoor/observability';
import { useObservability } from 'platform/observability';
import type { useSellerJourney } from 'sellDirect/graphql/useSellerJourney';

type SellerJourneyResponseForFirstResultTelemetry = Pick<
  ReturnType<typeof useSellerJourney>,
  'sellerJourney' | 'loading' | 'error'
>;

/**
 * After the first settled, successful SellerJourney fetch for a route address, sets RUM context and emits `seller-journey-first-result` once; polling updates are ignored per addressUUID until remount or address change.
 */
export const useTrackSellerJourneyFirstResult = (
  addressUUID: string | undefined,
  sellerJourneyResponse: SellerJourneyResponseForFirstResultTelemetry,
): void => {
  const { trackEvent } = useObservability();
  const loggedForAddressRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!addressUUID) {
      return;
    }
    if (sellerJourneyResponse.loading || sellerJourneyResponse.error) {
      return;
    }
    if (loggedForAddressRef.current === addressUUID) {
      return;
    }
    loggedForAddressRef.current = addressUUID;

    const sellerJourneyType = sellerJourneyResponse.sellerJourney?.__typename ?? 'null';

    addDatadogRUMGlobalContext({
      sellerJourneyFirstResultType: sellerJourneyType,
    });
    trackEvent('seller-journey-first-result', sellerJourneyType, {
      sellerJourneyType,
      addressUUID,
    });
  }, [
    addressUUID,
    sellerJourneyResponse.loading,
    sellerJourneyResponse.error,
    sellerJourneyResponse.sellerJourney,
    trackEvent,
  ]);
};
